import { CREATE_FEE, SIZE_PRICE_PER_HOUR } from '@hearth/shared';

/** The cost calculator's sums and chart geometry, pure so tests need no browser. Prices come from billing.ts. */

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** What a typical hosted 4 GB server costs every month, in dollars (checked against hosts' public prices, 2026-10-26). */
export const FLAT_PER_MONTH = 12;

const dollars = (money: number) => money / 10_000;
/** Hearth's Small size, the nearest to a 4 GB server: dollars an hour awake, and the one-time setup. */
export const HEARTH_PER_HOUR = dollars(SIZE_PRICE_PER_HOUR.small);
export const SETUP_FEE = dollars(CREATE_FEE);
/** Hours a month at which a flat plan starts to cost less than Hearth. */
export const BREAK_EVEN_HOURS = Math.round(FLAT_PER_MONTH / HEARTH_PER_HOUR);

export const DEFAULT_HOURS = 12;
/** A winter group: January, February, November and December. */
export const DEFAULT_MONTHS: readonly boolean[] = MONTH_NAMES.map((_, i) => [0, 1, 10, 11].includes(i));

const WEEKS_PER_YEAR = 52;

/** A point on the chart: months since the start, dollars paid so far. */
export type Point = readonly [number, number];

export interface Estimate {
  monthHours: number;
  /** Dollars in a month played. */
  monthCost: number;
  hearthTotal: number;
  flatTotal: number;
  /** The flat plan minus Hearth; negative when the flat plan is cheaper. */
  saved: number;
  /** The flat plan steps up by one month's price at the start of each month. */
  flatPath: Point[];
  /** Hearth: starts at the setup (no mark of its own), then the hours used in each month played. */
  hearthPath: Point[];
}

export function estimate(hoursPerWeek: number, months: readonly boolean[]): Estimate {
  const monthHours = (hoursPerWeek * WEEKS_PER_YEAR) / 12;
  const monthCost = monthHours * HEARTH_PER_HOUR;
  let paid = SETUP_FEE;
  const hearthPath: Point[] = [[0, SETUP_FEE]];
  months.forEach((played, i) => {
    if (played) paid += monthCost;
    hearthPath.push([i + 1, paid]);
  });
  const flatPath: Point[] = [[0, 0]];
  for (let m = 1; m <= 12; m++) flatPath.push([m - 1, FLAT_PER_MONTH * m], [m, FLAT_PER_MONTH * m]);
  const flatTotal = FLAT_PER_MONTH * 12;
  return { monthHours, monthCost, hearthTotal: paid, flatTotal, saved: flatTotal - paid, flatPath, hearthPath };
}

export const money = (dollarsAmount: number) => '$' + dollarsAmount.toFixed(2);

// Chart geometry in a 760 x 390 box: room on the left and below for the axis titles.
export const CHART = { width: 760, height: 390, left: 96, right: 96, top: 24, bottom: 66 } as const;

export interface Layout {
  bands: { x: number; w: number }[];
  grid: { y: number; label: string }[];
  months: { x: number; y: number; name: string }[];
  /** Where the axis titles sit: "Month" centred below, "Total Paid ($)" turned up the left. */
  axes: { x: { x: number; y: number }; y: { x: number; y: number } };
  flatPoints: string;
  hearthPoints: string;
  flatEnd: { x: number; y: number; labelY: number; label: string };
  hearthEnd: { x: number; y: number; labelY: number; label: string };
}

export function layout(e: Estimate, months: readonly boolean[]): Layout {
  const { width, height, left, right, top, bottom } = CHART;
  const plotW = width - left - right;
  const plotH = height - top - bottom;
  const rawMax = Math.max(e.flatTotal, e.hearthTotal) * 1.04;
  const step = [20, 25, 40, 50, 100, 200, 250, 500, 1000].find((s) => rawMax / s <= 6) ?? 1000;
  const yMax = Math.ceil(rawMax / step) * step;
  const x = (month: number) => left + (month / 12) * plotW;
  const y = (value: number) => top + plotH - (value / yMax) * plotH;
  const points = (path: Point[]) => path.map(([m, v]) => `${x(m).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

  const grid: Layout['grid'] = [];
  for (let v = 0; v <= yMax; v += step) grid.push({ y: y(v), label: '$' + v });

  // The two end labels sit beside their points; keep them apart when the totals are close.
  const flatLabelY = y(e.flatTotal) + 5;
  let hearthLabelY = y(e.hearthTotal) + 5;
  if (Math.abs(hearthLabelY - flatLabelY) < 17) hearthLabelY = hearthLabelY >= flatLabelY ? flatLabelY + 17 : flatLabelY - 17;

  return {
    bands: months.flatMap((played, i) => (played ? [{ x: x(i), w: plotW / 12 }] : [])),
    grid,
    months: MONTH_NAMES.map((name, i) => ({ x: x(i + 0.5), y: top + plotH + 22, name })),
    axes: { x: { x: left + plotW / 2, y: height - 10 }, y: { x: 16, y: top + plotH / 2 } },
    flatPoints: points(e.flatPath),
    hearthPoints: points(e.hearthPath),
    flatEnd: { x: x(12), y: y(e.flatTotal), labelY: flatLabelY, label: '$' + e.flatTotal },
    hearthEnd: { x: x(12), y: y(e.hearthTotal), labelY: hearthLabelY, label: money(e.hearthTotal) },
  };
}
